import React, { useEffect, useMemo, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { createPortal } from 'react-dom';
import DOMPurify from 'dompurify';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import { generateHTML } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { TextStyleKit } from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import { Placeholder } from '@tiptap/extensions';
import {
  AlignCenter, AlignJustify, AlignLeft, AlignRight, Baseline, Bold, ExternalLink, Highlighter, ImagePlus, Italic, Link as LinkIcon,
  List, ListOrdered, Quote, Redo2, RemoveFormatting, Strikethrough, Underline as UnderlineIcon, Undo2,
} from 'lucide-react';
import { ImageNode, EmbedNode } from 'features/dashboards/components/MediaNodes';
import { classifyMedia } from 'features/dashboards/media';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import AnswerMarkdown from 'shared/components/chat/AnswerMarkdown';
import { useAppDialog } from 'shared/context/AppDialogContext';

/**
 * Text cards: a document from the editor, rendered the same everywhere a
 * dashboard is shown. The server keeps only what these lists allow
 * (services/dashboards/richText.js) and the view sanitises again.
 */

// Values must match FONTS / SIZES in services/dashboards/richText.js.
export const FONTS = [
  ['', 'Default'],
  ['Inter, sans-serif', 'Inter'],
  ['Georgia, "Times New Roman", serif', 'Serif'],
  ['"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace', 'Mono'],
  ['system-ui, -apple-system, "Segoe UI", sans-serif', 'System'],
];
export const SIZES = ['', '12px', '14px', '16px', '18px', '20px', '24px', '30px', '36px', '48px'];
const BLOCK_STYLES = [['p', 'Text'], ['1', 'Title'], ['2', 'Heading'], ['3', 'Subheading'], ['4', 'Small heading']];
const TEXT_COLORS = [
  ['', 'Default'], ['var(--text-muted)', 'Muted'], ['var(--trodo-primary)', 'Brand'],
  ['#dc2626', 'Red'], ['#d97706', 'Amber'], ['#16a34a', 'Green'], ['#2563eb', 'Blue'], ['#7c3aed', 'Violet'],
];
const HIGHLIGHTS = [
  ['', 'None'], ['rgba(250, 204, 21, 0.35)', 'Yellow'], ['rgba(34, 197, 94, 0.3)', 'Green'],
  ['rgba(59, 130, 246, 0.3)', 'Blue'], ['rgba(239, 68, 68, 0.3)', 'Red'], ['rgba(255, 109, 41, 0.3)', 'Orange'],
];

export const EXTENSIONS = [
  StarterKit.configure({ heading: { levels: [1, 2, 3, 4] }, link: { openOnClick: false, autolink: true, protocols: ['http', 'https', 'mailto'] } }),
  TextStyleKit.configure({ backgroundColor: false, lineHeight: false }),
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  Highlight.configure({ multicolor: true }),
  ImageNode,
  EmbedNode,
];

/** Scoped typography: the app resets headings and lists, a text card puts them back. */
export const RICH_CSS = `
  .dash-rich { font-size: 14px; line-height: 1.55; color: var(--text-primary); word-wrap: break-word; }
  .dash-rich > * + * { margin-top: .55em; }
  .dash-rich h1 { font-size: 28px; font-weight: 700; line-height: 1.2; }
  .dash-rich h2 { font-size: 21px; font-weight: 650; line-height: 1.25; }
  .dash-rich h3 { font-size: 17px; font-weight: 600; line-height: 1.3; }
  .dash-rich h4 { font-size: 14px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: var(--text-muted); }
  .dash-rich ul { list-style: disc; padding-left: 1.4em; }
  .dash-rich ol { list-style: decimal; padding-left: 1.4em; }
  .dash-rich li > p { margin: 0; }
  .dash-rich blockquote { border-left: 3px solid var(--border-default); padding-left: .8em; color: var(--text-muted); }
  .dash-rich a { color: var(--trodo-primary); text-decoration: underline; text-underline-offset: 2px; }
  .dash-rich code { font-family: "JetBrains Mono", ui-monospace, monospace; font-size: .9em; background: var(--surface-sunken); padding: .1em .3em; border-radius: 4px; }
  .dash-rich pre { background: var(--surface-sunken); padding: .6em .8em; border-radius: 6px; overflow-x: auto; }
  .dash-rich pre code { background: none; padding: 0; }
  .dash-rich hr { border: 0; border-top: 1px solid var(--border-default); }
  .dash-rich mark { color: inherit; border-radius: 2px; padding: 0 .1em; }
  .dash-rich .ProseMirror { outline: none; min-height: 180px; }
  .dash-rich .dash-media-box { border: 1px dashed var(--border-default); border-radius: 6px; padding: .6em .8em; font-size: 12px; color: var(--text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .dash-rich .dash-media-box.ProseMirror-selectednode { border-color: var(--brand-primary); }
  .dash-rich .ProseMirror p.is-editor-empty:first-child::before { content: attr(data-placeholder); color: var(--text-muted); float: left; height: 0; pointer-events: none; }
`;

/** A card's document as safe HTML. */
export function docToHtml(doc) {
  try {
    return DOMPurify.sanitize(generateHTML(doc, EXTENSIONS), { ADD_ATTR: ['target'], FORBID_TAGS: ['style', 'img', 'iframe', 'form', 'input'] });
  } catch (_) {
    return '';
  }
}

/** Markdown (what Ask writes) as the editor's starting HTML. */
export function markdownToHtml(markdown) {
  return DOMPurify.sanitize(renderToStaticMarkup(<AnswerMarkdown text={markdown || ''} />));
}

/**
 * An image, a player or a link, drawn from its address alone: the frame's
 * source is rebuilt by classifyMedia, never read from the document. `fill`
 * when it is the card's main content, so it takes the card's height.
 */
export function Media({ url, alt, fill = false }) {
  const m = classifyMedia(url);
  if (!m) return null;
  if (m.kind === 'image') {
    return <img src={m.src} alt={alt || ''} loading="lazy" referrerPolicy="no-referrer" className={`mx-auto block max-w-full rounded ${fill ? 'h-full object-contain' : 'h-auto'}`} />;
  }
  if (m.kind === 'link') {
    return (
      <a href={m.src} target="_blank" rel="noopener noreferrer nofollow"
        className="flex items-center gap-2 rounded-md border border-[color:var(--border-default)] px-3 py-2 no-underline hover:bg-surface-hover">
        <ExternalLink className="h-3.5 w-3.5 shrink-0 text-[color:var(--text-muted)]" aria-hidden />
        <span className="min-w-0">
          <span className="block truncate text-[13px] font-medium text-[color:var(--text-primary)]">{alt || m.provider}</span>
          <span className="block truncate text-[11px] text-[color:var(--text-muted)]">{url}</span>
        </span>
      </a>
    );
  }
  return (
    <iframe title={alt || `${m.provider} ${m.kind}`} src={m.src} loading="lazy" referrerPolicy="strict-origin-when-cross-origin"
      sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
      allow="fullscreen; picture-in-picture; encrypted-media" allowFullScreen
      className={`block w-full rounded border-0 ${fill ? 'h-full min-h-[120px]' : 'aspect-video'}`} />
  );
}

const isMedia = (n) => n && (n.type === 'image' || n.type === 'embed');

/** Read-only text card: runs of text as sanitised HTML, images and embeds as Media between them. */
export function RichTextView({ doc }) {
  const parts = useMemo(() => {
    const out = [];
    for (const n of doc?.content || []) {
      if (isMedia(n)) out.push({ media: n });
      else if (out.length && out[out.length - 1].nodes) out[out.length - 1].nodes.push(n);
      else out.push({ nodes: [n] });
    }
    return out.map((p) => (p.nodes ? { html: docToHtml({ type: 'doc', content: p.nodes }) } : p));
  }, [doc]);
  // One image or video, maybe with a caption: it fills the card.
  const single = parts.filter((p) => p.media).length === 1 && parts.length <= 2;
  return (
    <>
      <style>{RICH_CSS}</style>
      <div className={`dash-rich ${single ? 'flex h-full flex-col' : ''}`}>
        {parts.map((p, i) => (p.media ? (
          // eslint-disable-next-line react/no-array-index-key -- the document's order is the key
          <div key={i} className={single ? 'min-h-0 flex-1' : ''}><Media url={p.media.attrs?.url} alt={p.media.attrs?.alt} fill={single} /></div>
        ) : (
          // eslint-disable-next-line react/no-array-index-key, react/no-danger -- sanitised by DOMPurify, built from a server-cleaned document
          <div key={i} className={single ? 'shrink-0' : ''} dangerouslySetInnerHTML={{ __html: p.html }} />
        )))}
      </div>
    </>
  );
}

const iconBtn = (on) => `inline-flex h-7 w-7 items-center justify-center rounded transition-colors ${on
  ? 'bg-surface-hover text-[color:var(--text-primary)]'
  : 'text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]'}`;

function Tool({ on, label, onClick, children }) {
  return (
    <button type="button" aria-label={label} title={label} aria-pressed={on} onMouseDown={(e) => e.preventDefault()} onClick={onClick} className={iconBtn(on)}>
      {children}
    </button>
  );
}

/** Colour swatches under a toolbar button. */
function Swatches({ label, icon: Icon, colors, current, onPick }) {
  const [open, setOpen] = useState(false);
  const btn = useRef(null);
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!open) return undefined;
    const r = btn.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: Math.min(r.left, window.innerWidth - 196) });
    const close = (e) => { if (!e.target.closest('[data-swatches]')) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') { e.preventDefault(); setOpen(false); } };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <>
      <button ref={btn} type="button" aria-label={label} title={label} aria-haspopup="true" aria-expanded={open}
        onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen((v) => !v)} className={iconBtn(Boolean(current))}>
        <span className="relative inline-flex">
          <Icon className="h-3.5 w-3.5" />
          <span className="absolute -bottom-1 left-0 right-0 h-[3px] rounded" style={{ background: current || 'transparent' }} />
        </span>
      </button>
      {open && pos ? createPortal(
        <div data-swatches role="menu" aria-label={label} style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 1400 }}
          className="grid w-[188px] grid-cols-4 gap-1.5 rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] p-2 shadow-lg">
          {colors.map(([value, name]) => (
            <button key={name} type="button" role="menuitemradio" aria-checked={(current || '') === value} title={name} aria-label={name}
              onMouseDown={(e) => e.preventDefault()} onClick={() => { onPick(value); setOpen(false); }}
              className={`flex h-9 flex-col items-center justify-center rounded border text-[9px] text-[color:var(--text-muted)] ${(current || '') === value ? 'border-[color:var(--brand-primary)]' : 'border-[color:var(--border-subtle)] hover:border-[color:var(--border-default)]'}`}>
              <span className="h-3.5 w-3.5 rounded-full border border-[color:var(--border-default)]" style={{ background: value || 'transparent' }} />
              {name}
            </button>
          ))}
        </div>,
        document.body,
      ) : null}
    </>
  );
}

/**
 * The text card editor: block style, font, size, marks, colour, highlight,
 * lists, quote, alignment, link.
 * @param {{ initial: object|string|null, onChange: (doc: object) => void }} props
 *   initial — a document, or markdown from Ask.
 */
export function RichTextEditor({ initial, onChange, placeholder = 'Write something…' }) {
  const { prompt: appPrompt } = useAppDialog();
  const editor = useEditor({
    extensions: [...EXTENSIONS, Placeholder.configure({ placeholder })],
    content: initial && typeof initial === 'object' ? initial : markdownToHtml(typeof initial === 'string' ? initial : ''),
    editorProps: { attributes: { 'aria-label': 'Text', class: 'min-h-[180px] px-3 py-2.5' } },
    onUpdate: ({ editor: e }) => onChange(e.getJSON()),
    onCreate: ({ editor: e }) => onChange(e.getJSON()),
  });

  const st = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      if (!e) return null;
      const ts = e.getAttributes('textStyle');
      const level = [1, 2, 3, 4].find((l) => e.isActive('heading', { level: l }));
      return {
        block: level ? String(level) : 'p',
        font: ts.fontFamily || '',
        size: ts.fontSize || '',
        color: ts.color || '',
        highlight: e.getAttributes('highlight').color || '',
        bold: e.isActive('bold'), italic: e.isActive('italic'), underline: e.isActive('underline'), strike: e.isActive('strike'),
        bullet: e.isActive('bulletList'), ordered: e.isActive('orderedList'), quote: e.isActive('blockquote'), link: e.isActive('link'),
        align: ['center', 'right', 'justify'].find((a) => e.isActive({ textAlign: a })) || 'left',
        undo: e.can().undo(), redo: e.can().redo(),
      };
    },
  });

  if (!editor || !st) return null;
  const c = () => editor.chain().focus();
  const setBlock = (v) => (v === 'p' ? c().setParagraph().run() : c().toggleHeading({ level: Number(v) }).run());
  const setLink = async () => {
    if (st.link) { c().extendMarkRange('link').unsetLink().run(); return; }
    const href = await appPrompt('Link address', { title: 'Add link', placeholder: 'https://', confirmLabel: 'Add' });
    if (!href || !/^(https?:\/\/|mailto:)/i.test(href.trim())) return;
    c().extendMarkRange('link').setLink({ href: href.trim() }).run();
  };
  // An image, a video, a Figma or Google document, or a link card — by its address.
  const addMedia = async () => {
    const url = await appPrompt('An image, a YouTube, Vimeo or Loom video, a Figma file, a Google Doc, Sheet or Slides, or any page to link to.', {
      title: 'Add image, video or embed', placeholder: 'https://', confirmLabel: 'Add',
    });
    const m = classifyMedia(url);
    if (!m) return;
    c().insertContent({ type: m.kind === 'image' ? 'image' : 'embed', attrs: { url: url.trim() } }).run();
  };

  return (
    <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] focus-within:border-[color:var(--brand-primary)]">
      <style>{RICH_CSS}</style>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-[color:var(--border-subtle)] bg-[color:var(--surface-sunken)] px-1.5 py-1">
        <div className="w-[128px]"><BrandDropdown compact ariaLabel="Style" value={st.block} onChange={setBlock} options={BLOCK_STYLES.map(([v, l]) => ({ value: v, label: l }))} /></div>
        <div className="w-[96px]"><BrandDropdown compact ariaLabel="Font" value={st.font} onChange={(v) => (v ? c().setFontFamily(v).run() : c().unsetFontFamily().run())} options={FONTS.map(([v, l]) => ({ value: v, label: l }))} /></div>
        <div className="w-[76px]"><BrandDropdown compact ariaLabel="Size" value={st.size} onChange={(v) => (v ? c().setFontSize(v).run() : c().unsetFontSize().run())} options={SIZES.map((v) => ({ value: v, label: v ? v.replace('px', '') : 'Size' }))} /></div>
        <span className="mx-1 h-5 w-px bg-[color:var(--border-subtle)]" />
        <Tool label="Bold" on={st.bold} onClick={() => c().toggleBold().run()}><Bold className="h-3.5 w-3.5" /></Tool>
        <Tool label="Italic" on={st.italic} onClick={() => c().toggleItalic().run()}><Italic className="h-3.5 w-3.5" /></Tool>
        <Tool label="Underline" on={st.underline} onClick={() => c().toggleUnderline().run()}><UnderlineIcon className="h-3.5 w-3.5" /></Tool>
        <Tool label="Strikethrough" on={st.strike} onClick={() => c().toggleStrike().run()}><Strikethrough className="h-3.5 w-3.5" /></Tool>
        <Swatches label="Text colour" icon={Baseline} colors={TEXT_COLORS} current={st.color} onPick={(v) => (v ? c().setColor(v).run() : c().unsetColor().run())} />
        <Swatches label="Highlight" icon={Highlighter} colors={HIGHLIGHTS} current={st.highlight} onPick={(v) => (v ? c().setHighlight({ color: v }).run() : c().unsetHighlight().run())} />
        <span className="mx-1 h-5 w-px bg-[color:var(--border-subtle)]" />
        <Tool label="Align left" on={st.align === 'left'} onClick={() => c().setTextAlign('left').run()}><AlignLeft className="h-3.5 w-3.5" /></Tool>
        <Tool label="Align centre" on={st.align === 'center'} onClick={() => c().setTextAlign('center').run()}><AlignCenter className="h-3.5 w-3.5" /></Tool>
        <Tool label="Align right" on={st.align === 'right'} onClick={() => c().setTextAlign('right').run()}><AlignRight className="h-3.5 w-3.5" /></Tool>
        <Tool label="Justify" on={st.align === 'justify'} onClick={() => c().setTextAlign('justify').run()}><AlignJustify className="h-3.5 w-3.5" /></Tool>
        <span className="mx-1 h-5 w-px bg-[color:var(--border-subtle)]" />
        <Tool label="Bulleted list" on={st.bullet} onClick={() => c().toggleBulletList().run()}><List className="h-3.5 w-3.5" /></Tool>
        <Tool label="Numbered list" on={st.ordered} onClick={() => c().toggleOrderedList().run()}><ListOrdered className="h-3.5 w-3.5" /></Tool>
        <Tool label="Quote" on={st.quote} onClick={() => c().toggleBlockquote().run()}><Quote className="h-3.5 w-3.5" /></Tool>
        <Tool label={st.link ? 'Remove link' : 'Link'} on={st.link} onClick={setLink}><LinkIcon className="h-3.5 w-3.5" /></Tool>
        <Tool label="Image, video or embed" onClick={addMedia}><ImagePlus className="h-3.5 w-3.5" /></Tool>
        <Tool label="Clear formatting" onClick={() => c().unsetAllMarks().clearNodes().unsetTextAlign().run()}><RemoveFormatting className="h-3.5 w-3.5" /></Tool>
        <span className="ml-auto" />
        <Tool label="Undo" onClick={() => st.undo && c().undo().run()}><Undo2 className="h-3.5 w-3.5" /></Tool>
        <Tool label="Redo" onClick={() => st.redo && c().redo().run()}><Redo2 className="h-3.5 w-3.5" /></Tool>
      </div>
      <div className="dash-rich max-h-[50vh] overflow-y-auto bg-surface">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
